import type { ReportParameters, Reports, ReportTypes } from "./report";

/** Client reports rebuilt live from Redux while a run is active. */
export type LiveClientReportType =
  | "profit/loss"
  | "profit/loss-comparison"
  | "sales-tax";

/**
 * Unified UI state for running a report (sync or async).
 * Async flows set `loading` / `error` with `reportType`; UI shows spinner + Alert when
 * `result.reportType` matches the selected report (see Content.tsx).
 *
 * Success is either a one-shot `report` snapshot, or a live run that stores frozen
 * `parameters` so nested views can select without a separate appliedParameters state.
 */
export type ReportRunResult =
  | { status: "idle" }
  | { status: "loading"; reportType: ReportTypes }
  | { status: "error"; reportType: ReportTypes; message: string }
  | {
      status: "success";
      report: Reports;
      staleReportWarning?: boolean;
    }
  | {
      status: "success";
      reportType: LiveClientReportType;
      parameters: ReportParameters;
    };

export function isReportRunIdle(r: ReportRunResult): r is { status: "idle" } {
  return r.status === "idle";
}

export function isReportRunLoading(
  r: ReportRunResult
): r is { status: "loading"; reportType: ReportTypes } {
  return r.status === "loading";
}

export function isReportRunError(
  r: ReportRunResult
): r is { status: "error"; reportType: ReportTypes; message: string } {
  return r.status === "error";
}

export function isReportRunSuccess(
  r: ReportRunResult
): r is Extract<ReportRunResult, { status: "success" }> {
  return r.status === "success";
}

export function isLiveReportRunSuccess(r: ReportRunResult): r is {
  status: "success";
  reportType: LiveClientReportType;
  parameters: ReportParameters;
} {
  return r.status === "success" && "parameters" in r;
}

export function isSnapshotReportRunSuccess(r: ReportRunResult): r is {
  status: "success";
  report: Reports;
  staleReportWarning?: boolean;
} {
  return r.status === "success" && "report" in r;
}

/** Marks a successful snapshot report run as stale after in-report edits. */
export function markReportRunStale(current: ReportRunResult): ReportRunResult {
  if (!isSnapshotReportRunSuccess(current)) return current;
  return { ...current, staleReportWarning: true };
}

export function errorMessageFromUnknown(e: unknown): string {
  if (
    e &&
    typeof e === "object" &&
    "message" in e &&
    typeof (e as { message: unknown }).message === "string"
  ) {
    return (e as { message: string }).message;
  }
  return "Failed to load report.";
}
